<template>
  <!--
    FrameTracker's Delivery Log driver sheet (rptDeliveryRep.dfm ppReport1).

    WHAT prints comes from the layout's `sheet` structure (column headings, the
    Shipvia group header, each stop, the summary); WHERE it prints is the .dfm's
    own geometry: every band is a box of its printed height and every item sits
    at its mmLeft / mmTop / mmWidth, slot by slot in the structure's order, so the
    sheet a driver fills in by hand looks like the one they know. Letter paper,
    quarter-inch margins, 203.2 mm printable width (ParentWidth items). An item
    beyond the .dfm's slots is placed in a plain flow after the last one.

    The header band is the table head, so a browser repeats it on every printed
    page as ReportBuilder does. The footer band (print date and time, Page n of N)
    is drawn by the page margin boxes ReportRunner sets; see printPage.js.
  -->
  <div class="ds-sheet">
    <table class="ds-pages">
      <thead>
      <tr>
        <td>
          <!-- ppHeaderBand1 (dfm:276-368) -->
          <div class="ds-band" style="height: 21.696mm">
            <div class="ds-at ds-head" style="left: 0; top: 7.673mm; width: 203.2mm; height: 4.064mm; text-align: center">{{ heading }}</div>
            <div v-if="company" class="ds-at ds-company" :style="at(163.513, 2.117, 34.131, 4.498)">{{ company }}</div>
            <div
                v-for="(col, i) in sheet.columnHeadings || []"
                :key="'h' + i"
                class="ds-at ds-9"
                :style="at(headingSlot(i), 15.875, 22.225, 3.969)">{{ col.label }}</div>
            <div class="ds-at ds-line" :style="at(0, 20.638, 203.2, 1.058)"></div>
          </div>
        </td>
      </tr>
      </thead>

      <template v-for="group in groups" :key="group.key">
        <tbody>
        <tr class="ds-keep-next">
          <td>
            <!-- ppGroupHeaderBand1 (dfm:1367-1569) -->
            <div class="ds-band" style="height: 34.66mm">
              <template v-for="(line, l) in sheet.groupHeader || []" :key="'gl' + l">
                <template v-for="(item, i) in line" :key="'gi' + l + '-' + i">
                  <template v-if="item.label !== undefined">
                    <div class="ds-at" :class="'ds-' + ghSlot(l, i).size" :style="at(ghSlot(l, i).labelLeft, ghSlot(l, i).top, ghSlot(l, i).labelWidth, ghSlot(l, i).height)">{{ item.label }}</div>
                    <div
                        v-if="item.text !== undefined"
                        class="ds-at ds-bold"
                        :class="'ds-' + ghSlot(l, i).size"
                        :style="at(ghSlot(l, i).valueLeft, ghSlot(l, i).top, ghSlot(l, i).valueWidth, ghSlot(l, i).height)">{{ render(item.text, group.rows[0]) }}</div>
                    <div
                        v-if="item.blank"
                        class="ds-at ds-line"
                        :style="at(ghSlot(l, i).lineLeft, ghSlot(l, i).lineTop, ghSlot(l, i).lineWidth, 1.852)"></div>
                  </template>
                  <!-- A bare text item ('Lunch ( Y / N )') sits where a label would. -->
                  <div
                      v-else
                      class="ds-at ds-pre"
                      :class="'ds-' + ghSlot(l, i).size"
                      :style="at(ghSlot(l, i).labelLeft, ghSlot(l, i).top, ghSlot(l, i).textWidth, ghSlot(l, i).height)">{{ render(item.text, group.rows[0]) }}</div>
                </template>
              </template>
              <div class="ds-at ds-line-double" :style="at(0, 32.544, 203.2, 3.969)"></div>
            </div>
          </td>
        </tr>

        <tr v-for="(row, r) in group.rows" :key="r" class="ds-keep">
          <td>
            <!-- ppDetailBand1, one stop (dfm:370-893) -->
            <div class="ds-band" style="height: 47.625mm">
              <div
                  v-for="(item, i) in stop.top || []"
                  :key="'t' + i"
                  class="ds-at"
                  :class="topSlot(i).size === 8 ? 'ds-8' : 'ds-9'"
                  :style="at(topSlot(i).left, 1.323, 22.225, 3.704)">{{ plain(row[item.key]) }}</div>

              <div v-if="stop.address" class="ds-at ds-memo" :style="at(44.715, 0.529, 76.2, 23.019)">
                <div v-for="(line, j) in memoLines(row)" :key="j">{{ line }}</div>
              </div>

              <template v-if="stop.cod && showCod(row)">
                <div class="ds-at ds-11 ds-bold" :style="at(3.704, 12.965, 11.906, 4.551)">{{ stop.cod.label }}</div>
                <div class="ds-at ds-11 ds-bold" :style="at(16.404, 12.965, 24.871, 4.551)">{{ fmt(stop.cod.format, row[stop.cod.key]) }}</div>
              </template>

              <template v-if="stop.signature">
                <div class="ds-at ds-12" :style="at(123.825, 12.965, 5.821, 4.763)" style="text-align: center">{{ stop.signature.text }}</div>
                <div v-if="stop.signature.blank" class="ds-at ds-line" :style="at(130.969, 17.727, 66.675, 2.117)"></div>
              </template>

              <!-- Quantity grid: caption, figure, rule under the figure; a blank item is a write-in line. -->
              <template v-for="(qrow, q) in stop.quantities || []" :key="'q' + q">
                <template v-for="(item, i) in qrow" :key="'qi' + q + '-' + i">
                  <template v-if="item.blank">
                    <div class="ds-at ds-9" :style="at(qtySlot(q, i).labelLeft, qtySlot(q, i).top, 15.346, 3.704)">{{ item.label }}</div>
                    <div class="ds-at ds-line" :style="at(qtySlot(q, i).left, qtySlot(q, i).top + 4.233, 197.38 - qtySlot(q, i).left, 1.852)"></div>
                  </template>
                  <template v-else>
                    <div class="ds-at ds-9" :style="at(qtySlot(q, i).labelLeft, qtySlot(q, i).top, 15.346, 3.969)" style="text-align: right">{{ item.label }}</div>
                    <div class="ds-at ds-8" :style="at(qtySlot(q, i).left, qtySlot(q, i).top, 10.054, 3.969)" style="text-align: center">{{ qty(row[item.key], item.format) }}</div>
                    <div class="ds-at ds-line" :style="at(qtySlot(q, i).lineLeft, qtySlot(q, i).top + 4.233, 10.054, 2.117)"></div>
                  </template>
                </template>
              </template>

              <div v-if="stop.boxed" class="ds-at ds-hatch" :style="at(0, 42.069, 203.2, 3.175)"></div>
            </div>
          </td>
        </tr>
        </tbody>
      </template>

      <tbody v-if="sheet.summary">
      <tr class="ds-keep">
        <td>
          <!-- ppSummaryBand1 (dfm:942-1366) -->
          <div class="ds-band" style="height: 42.863mm">
            <template v-if="summary.count">
              <div class="ds-at ds-9" :style="at(4.498, 1.852, 11.113, 3.704)">{{ summary.count.label }}</div>
              <div class="ds-at ds-10" :style="at(18.521, 1.852, 17.198, 3.969)" style="text-align: right">{{ fmt(summary.count.format || '#,0', rows.length) }}</div>
              <div class="ds-at ds-10" :style="at(38.629, 1.852, 13.229, 3.969)">{{ summary.count.caption }}</div>
            </template>
            <div v-if="summary.boxed" class="ds-at ds-box" :style="at(0, 7.144, 203.2, 16.933)"></div>
            <template v-for="(qrow, q) in summary.quantities || []" :key="'sq' + q">
              <template v-for="(item, i) in qrow" :key="'sqi' + q + '-' + i">
                <div class="ds-at ds-9" :style="at(qtySlot(q, i).labelLeft, qtySlot(q, i).sumTop, 15.346, 3.969)" style="text-align: right">{{ item.label }}</div>
                <template v-if="!item.blank">
                  <div class="ds-at ds-9" :style="at(qtySlot(q, i).left, qtySlot(q, i).sumTop, 10.054, 3.704)" style="text-align: center">{{ fmt(item.format || summary.format || '#,0', total(item.key)) }}</div>
                  <div class="ds-at ds-line" :style="at(qtySlot(q, i).left, qtySlot(q, i).sumTop + 3.969, 10.054, 2.117)"></div>
                </template>
              </template>
            </template>
          </div>
        </td>
      </tr>
      </tbody>
    </table>

    <!-- ppFooterBand1 on screen; in print the page margin boxes draw it. -->
    <div class="ds-screen-footer no-print">
      <span>{{ footerLeft }}</span>
      <span>{{ footerRight }}</span>
    </div>
  </div>
</template>

<script>
import {formatFloat, formatWith} from './delphiFormat.js'

// The .dfm's slots, in the order the sheet lists its items.

// Route #, Invoice #, Order #: header captions (ppLabel19/17/18) and the stop's
// top line (ppDBText11/9/10, RouteNo in 8 pt, the numbers in 9 pt).
const TOP_SLOTS = [{left: 1.323, size: 8}, {left: 127.529, size: 9}, {left: 151.077, size: 9}]

// ppGroupHeaderBand1, line by line: Invoice Date: / Shipvia: (11 pt, values
// bold), Time In: / Time Out: over lines, Driver: over a line, then the two
// Y/N texts.
const GROUP_SLOTS = [
  [
    {top: 3.704, size: 11, height: 4.445, labelLeft: 10.054, labelWidth: 22.648, valueLeft: 34.131, valueWidth: 48.26},
    {top: 3.704, size: 11, height: 4.445, labelLeft: 97.367, labelWidth: 13.801, valueLeft: 112.977, valueWidth: 71.438},
  ],
  [
    {top: 13.229, size: 9, height: 3.704, labelLeft: 17.198, labelWidth: 11.377, lineLeft: 31.485, lineTop: 17.463, lineWidth: 31.75},
    {top: 13.229, size: 9, height: 3.704, labelLeft: 73.554, labelWidth: 14.023, lineLeft: 91.281, lineTop: 17.463, lineWidth: 31.75},
  ],
  [
    {top: 25.665, size: 9, height: 3.704, labelLeft: 19.315, labelWidth: 9.26, lineLeft: 31.485, lineTop: 29.369, lineWidth: 75.936},
    {top: 25.665, size: 9, height: 3.704, labelLeft: 116.946, textWidth: 32.808},
    {top: 25.665, size: 9, height: 3.704, labelLeft: 161.396, textWidth: 37.042},
  ],
]

// The quantity grid, two rows: the caption right-justified before each figure
// (ppLabel8-15), the figure (ppDBText1-8) and the rule under it (ppLine4-11;
// ppLine5 sits 0.265 mm left of its figure). The third slot of the second row
// is 'Comments' over ppLine16. sumTop is the same grid in the summary band
// (ppDBCalc1-8, ppLine17-24).
const QTY_SLOTS = [
  [
    {labelLeft: 9.79, left: 26.723, lineLeft: 26.458, top: 25.4, sumTop: 8.996},
    {labelLeft: 43.656, left: 60.325, lineLeft: 60.325, top: 25.4, sumTop: 8.996},
    {labelLeft: 74.877, left: 92.075, lineLeft: 92.075, top: 25.4, sumTop: 8.996},
    {labelLeft: 107.156, left: 124.09, lineLeft: 124.09, top: 25.4, sumTop: 8.996},
    {labelLeft: 138.642, left: 155.311, lineLeft: 155.311, top: 25.4, sumTop: 8.996},
    {labelLeft: 170.657, left: 187.325, lineLeft: 187.325, top: 25.4, sumTop: 8.996},
  ],
  [
    {labelLeft: 9.79, left: 26.723, lineLeft: 26.723, top: 33.602, sumTop: 16.933},
    {labelLeft: 43.656, left: 60.325, lineLeft: 60.325, top: 33.602, sumTop: 16.933},
    {labelLeft: 107.156, left: 124.09, lineLeft: 124.09, top: 33.602, sumTop: 16.933},
  ],
]

export default {
  name: "DeliverySheet",
  props: {
    rows: {type: Array, required: true},
    // The layout's `sheet` structure.
    sheet: {type: Object, required: true},
    // lblHead, already rendered from the layout's heading.
    heading: {type: String, default: ''},
    // lblCompany (Sys.Company), or '' when the page header leaves it out.
    company: {type: String, default: ''},
    // Renders a template against the run on screen; the row is the group's first.
    render: {type: Function, required: true},
    // The footer band on screen: print date and time, page number.
    footerLeft: {type: String, default: ''},
    footerRight: {type: String, default: ''},
  },
  computed: {
    stop() {
      return this.sheet.stop || {}
    },
    summary() {
      return this.sheet.summary || {}
    },
    /** ppGroup1 breaks wherever the group field changes, in the order the rows came. */
    groups() {
      const key = this.sheet.groupBy
      const groups = []
      let current = null
      this.rows.forEach((row, i) => {
        const value = key ? row[key] : null
        if (!current || value !== current.value) {
          current = {key: i, value, rows: []}
          groups.push(current)
        }
        current.rows.push(row)
      })
      return groups
    },
  },
  methods: {
    at(left, top, width, height) {
      return {left: left + 'mm', top: top + 'mm', width: width + 'mm', height: height + 'mm'}
    },
    headingSlot(i) {
      return TOP_SLOTS[i] ? TOP_SLOTS[i].left : 1.323 + i * 50
    },
    topSlot(i) {
      return TOP_SLOTS[i] || {left: 1.323 + i * 50, size: 9}
    },
    ghSlot(line, i) {
      const slot = (GROUP_SLOTS[line] || [])[i]
      if (slot) return {textWidth: 60, labelWidth: 25, valueWidth: 50, lineWidth: 30, valueLeft: slot.labelLeft + 25, lineLeft: slot.labelLeft + 25, lineTop: slot.top + 4.234, ...slot}
      const top = 3.704 + line * 10.98
      const left = 10 + i * 65
      return {top, size: 9, height: 3.704, labelLeft: left, labelWidth: 25, valueLeft: left + 25, valueWidth: 38,
        lineLeft: left + 25, lineTop: top + 4.234, lineWidth: 38, textWidth: 63}
    },
    qtySlot(row, i) {
      const slot = (QTY_SLOTS[row] || [])[i]
      if (slot) return slot
      const base = (QTY_SLOTS[row] || QTY_SLOTS[0])[0]
      const left = 26.723 + i * 33
      return {labelLeft: left - 16.933, left, lineLeft: left, top: base.top, sumTop: base.sumTop}
    },
    fmt(format, value) {
      return format ? formatWith(format, value) : this.plain(value)
    },
    /** A field as a TppDBText with no DisplayFormat prints it: as stored. */
    plain(value) {
      return value === null || value === undefined ? '' : String(value)
    },
    /** A summed quantity with no DisplayFormat prints as AsString: 2, not 2.0000. */
    qty(value, format) {
      if (format) return formatWith(format, value)
      if (value === null || value === undefined || value === '') return ''
      const num = Number(value)
      return Number.isFinite(num) ? String(num) : String(value)
    },
    total(key) {
      return this.rows.reduce((t, r) => t + (parseFloat(r[key]) || 0), 0)
    },
    /** cod.showWhenNotNull: lblCOD and the amount print only on COD terms. */
    showCod(row) {
      const cod = this.stop.cod
      if (!cod.showWhenNotNull) return true
      const v = row[cod.key]
      return v !== null && v !== undefined && v !== ''
    },
    /**
     * ppMemo1: the address field split on its line separator. Without the
     * field, the lines are built as ppDetailBand1BeforePrint builds them.
     */
    memoLines(row) {
      const address = this.stop.address
      const value = row[address.key]
      if (value !== undefined && value !== null) {
        return String(value).split(address.lineSeparator || '\n')
      }
      const s = v => (v === null || v === undefined ? '' : String(v))
      const lines = [s(row.ShipCompany)]
      if (s(row.ShipAddress1) > '') lines.push(s(row.ShipAddress1))
      if (s(row.ShipAddress2) > '') lines.push(s(row.ShipAddress2))
      if (s(row.ShipCity) > '') lines.push(s(row.ShipCity).trim() + ', ' + s(row.ShipState) + ' ' + s(row.ShipZip))
      else lines.push(s(row.ShipState) + ' ' + s(row.ShipZip))
      lines.push('Phone #: ' + s(row.Phone))
      return lines
    },
    formatFloat,
  },
}
</script>

<style scoped>
.ds-sheet {
  width: 203.2mm;
  margin: 0 auto;
  font-family: Arial, Helvetica, sans-serif;
  color: #000;
  background: #fff;
}

.ds-pages {
  border-collapse: collapse;
  width: 203.2mm;
}

.ds-pages td {
  padding: 0;
  border: none;
}

.ds-band {
  position: relative;
  width: 203.2mm;
  overflow: hidden;
}

.ds-at {
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  line-height: 1.15;
}

.ds-pre {
  white-space: pre;
}

.ds-8 { font-size: 8pt; }
.ds-9 { font-size: 9pt; }
.ds-10 { font-size: 10pt; }
.ds-11 { font-size: 11pt; }
.ds-12 { font-size: 12pt; }
.ds-bold { font-weight: bold; }

.ds-head {
  font-size: 10pt;
}

/* lblCompany: Arial 11 bold italic, right-justified. */
.ds-company {
  font-size: 11pt;
  font-weight: bold;
  font-style: italic;
  text-align: right;
}

/* ppMemo1: Arial 10, no wrap, clipped to its box. */
.ds-memo {
  font-size: 10pt;
  white-space: pre;
}

/* TppLine draws along the top of its box; weight 0.75 pt. */
.ds-line {
  border-top: 0.75pt solid #000;
}

.ds-line-double {
  border-top: 2.25pt double #000;
}

/* ppShape2: clear brush, black pen. */
.ds-box {
  border: 1px solid #000;
}

/* ppShape1: silver diagonal cross-hatch under each stop. */
.ds-hatch {
  border: 1px solid #000;
  background:
    repeating-linear-gradient(45deg, #c0c0c0 0, #c0c0c0 0.5px, transparent 0.5px, transparent 4px),
    repeating-linear-gradient(-45deg, #c0c0c0 0, #c0c0c0 0.5px, transparent 0.5px, transparent 4px);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.ds-screen-footer {
  display: flex;
  justify-content: space-between;
  border-top: 0.75pt solid #000;
  margin-top: 4mm;
  padding-top: 1mm;
  font-size: 9pt;
}

@media print {
  .ds-keep {
    break-inside: avoid;
  }

  /* KeepTogether: a group header never ends a page alone. */
  .ds-keep-next {
    break-inside: avoid;
    break-after: avoid;
  }
}
</style>
